import { useCallback, useMemo } from "react";

import { COLORS } from "@/styles/colors";

import { useConvertPaymentMethodText } from "../../../../_hooks";

import type * as Repository from "../../../../_repository";
import type * as Type from "../type";

type Props = {
	// 検索日
	searchDate: string;
	// オプションリスト
	optionPmsActiveListQueryData: Repository.Query.QueryData.OptionPmsList.OptionPmsActiveListQueryData;
	// オプションカラーリスト
	optionPmsColorListQueryData: Repository.Query.QueryData.OptionPmsColorListQueryData;
};
type Result = {
	viewData: Type.ViewData;
};

export const useViewDataConverter = ({
	searchDate,
	optionPmsActiveListQueryData: {
		optionPmsList: { options },
	},
	optionPmsColorListQueryData: {
		optionPmsColorList: { optionColors },
	},
}: Props): Result => {
	/**
	 * オプションへ変換
	 */
	const onConvertOption = useCallback(
		(_optionID: string, optionName: string, subOptionName?: string | null) => ({
			id: _optionID,
			nameText: subOptionName ? `${optionName} | ${subOptionName}` : optionName,
			color:
				optionColors.find(({ optionID: __optionID }) => _optionID === __optionID)?.color ??
				COLORS.CHILLNN_PINK[50],
		}),
		[optionColors],
	);

	/**
	 * 決済方法をテキストへ変換
	 */
	const { onConvertPaymentMethodText } = useConvertPaymentMethodText();

	/**
	 * 時間をテキストへ変換
	 */
	const onConvertAt = useCallback((startAt?: string | null, endAt?: string | null) => {
		return endAt ? `${startAt} - ${endAt}` : startAt ?? "-";
	}, []);

	return useMemo(
		() => ({
			viewData: {
				// 日付
				searchDateText: searchDate,
				// リスト件数
				listCountText: `${options.length}件`,
				// テーブル行データ
				rowDataList: options.map(
					({
						optionID: _optionID,
						optionName,
						subOptionName,
						optionStartAt,
						optionEndAt,
						guestName,
						guestTel,
						paymentMethod,
						memo,
					}) => ({
						option: onConvertOption(_optionID, optionName, subOptionName),
						atText: onConvertAt(optionStartAt, optionEndAt),
						guestNameText: `${guestName} 様`,
						guestTelText: guestTel ?? "",
						paymentMethodText: onConvertPaymentMethodText(paymentMethod ?? null),
						memo: memo ?? "",
					}),
				),
				// テーブル行データが存在するか
				hasRowDataList: options.length > 0,
			},
		}),
		[onConvertAt, onConvertOption, onConvertPaymentMethodText, options, searchDate],
	);
};
